// ============================================================================
// DescriptionSection — unified tabbed panel (Problem | Requirements | Concept)
// Replaces the old stacked Description + Requirements + Concept cards.
// Usage in Challenge.js: <DescriptionSection challenge={challenge} c={c} theme={theme} />
// (Requirements + Concept no longer need to be rendered separately in Challenge.js)
// ============================================================================

import React, { useState } from 'react';

function DescriptionSection({ challenge, c, theme }) {
  // Fallback colors if c is not provided
  const colors = c || {
    card: theme === 'dark' ? '#1a1f3a' : '#ffffff',
    border: theme === 'dark' ? '#2d3748' : '#e2e8f0',
    text: theme === 'dark' ? '#e2e8f0' : '#1a202c',
    secondary: theme === 'dark' ? '#cbd5e0' : '#4a5568',
    accent: '#4f8ef7',
    accentBg: theme === 'dark' ? 'rgba(79, 142, 247, 0.15)' : 'rgba(79, 142, 247, 0.1)',
  };

  const description  = challenge?.description || '';
  const requirements = challenge?.requirements || [];
  const concept      = challenge?.concept || '';
  const docLink      = challenge?.doc_link;

  // ── Prerequisites ──
  // Comes from the DB as jsonb (array) or as a JSON string — handle both.
  const prerequisites = (() => {
    const raw = challenge?.prerequisites;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; }
      catch { return []; }
    }
    return [];
  })();

  const SETUP_PREFIX = /^(ORG SETUP:|The trigger in the starter code)/i;
  const setupItems     = prerequisites.filter(p => SETUP_PREFIX.test(p));
  const knowledgeItems = prerequisites.filter(p => !SETUP_PREFIX.test(p));

  // Expanded by default only when there's org setup the learner MUST do,
  // otherwise collapsed so it stays out of the way.
  const [prereqOpen, setPrereqOpen] = useState(setupItems.length > 0);

  // Which tabs actually have content
  const tabs = [];
  if (description)          tabs.push('problem');
  if (requirements.length)  tabs.push('requirements');
  if (concept)               tabs.push('concept');

  const tabLabels = {
    problem:      '📝 Problem',
    requirements: `✅ Requirements${requirements.length ? ` (${requirements.length})` : ''}`,
    concept:      '📚 Concept',
  };

  // Default to Requirements — that's what most people actually need first.
  // Fall back to whatever's available if Requirements is empty.
  const defaultTab = tabs.includes('requirements') ? 'requirements' : (tabs[0] || null);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [activeProblemSection, setActiveProblemSection] = useState(0);

  if (tabs.length === 0) return null;

  // One-line summary pulled from the first non-empty line of the description
  const firstLine = description.split('\n').map(l => l.trim()).find(l => l.length > 0) || '';
  const summaryLine = firstLine.length > 140 ? firstLine.slice(0, 140).trim() + '…' : firstLine;

  // ── Split the Problem description into sub-sections ──
  // Kept deliberately minimal: only "Real-World Scenarios" gets its own tab.
  // Everything else (intro story, "The Problem:", "The Scale:", etc.) stays
  // together under one "Overview" tab — splitting on every little label
  // (Challenge:, The Problem:, The Scale:) caused tab sprawl.
  const HEADER_PREFIXES = ['Real-World Scenarios'];

  const parseDescription = (text) => {
    if (!text) return [];
    const lines = text.split('\n');
    const sections = [];
    let current = null;
    const seenCount = {};

    const matchHeader = (line) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      for (const prefix of HEADER_PREFIXES) {
        const re = new RegExp(`^${prefix}\\b`, 'i');
        if (re.test(trimmed)) {
          return { label: prefix, rest: '' };
        }
      }
      return null;
    };

    lines.forEach((line) => {
      const header = matchHeader(line);
      if (header) {
        if (current) sections.push(current);
        let label = header.label;
        seenCount[label] = (seenCount[label] || 0) + 1;
        if (seenCount[label] > 1) label = `${label} ${seenCount[label]}`;
        current = { title: label, content: header.rest ? [header.rest] : [] };
      } else if (current) {
        current.content.push(line);
      } else {
        current = { title: 'Overview', content: [line] };
      }
    });
    if (current) sections.push(current);

    return sections
      .map(s => ({ ...s, content: s.content.join('\n') }))
      .filter(s => s.content.trim().length > 0);
  };

  const renderInline = (line) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return parts.map((part, idx) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={idx} style={{ color: colors.text, fontWeight: 700 }}>{part.slice(2, -2)}</strong>
        : part
    );
  };

  const formatSectionContent = (content) => {
    return content.split('\n').filter(l => l.trim()).map((line, i) => {
      const trimmed = line.trim();
      if (trimmed.match(/^[-•*]\s/) && !trimmed.startsWith('**')) {
        return (
          <div key={i} style={{ marginLeft: '12px', marginBottom: '4px', fontSize: '12px', color: colors.secondary, lineHeight: '1.5' }}>
            {renderInline(trimmed.replace(/^[-•*]\s/, ''))}
          </div>
        );
      }
      const isHeading = trimmed.startsWith('**') && trimmed.endsWith('**');
      return (
        <p key={i} style={{ fontSize: '13px', lineHeight: 1.7, color: colors.secondary, marginBottom: '6px', marginTop: isHeading ? '14px' : 0 }}>
          {renderInline(trimmed)}
        </p>
      );
    });
  };

  const problemSections = parseDescription(description);

  // Amber palette for the org-setup warning state of the prerequisites callout
  const warn = theme === 'dark'
    ? { bg: 'rgba(245, 158, 11, 0.10)', border: 'rgba(245, 158, 11, 0.35)', text: '#fbbf24' }
    : { bg: '#fffbeb',                  border: '#fcd34d',                  text: '#92400e' };

  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '8px', marginBottom: '14px', overflow: 'hidden', transition: 'background 0.25s, border-color 0.25s' }}>

      {/* Always-visible one-line summary */}
      {summaryLine && (
        <div style={{ padding: '14px 16px 12px' }}>
          <p style={{ fontSize: '13px', color: colors.text, lineHeight: 1.6, margin: 0, fontWeight: 500 }}>{summaryLine}</p>
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '4px', padding: summaryLine ? '0 12px' : '12px 12px 0', borderTop: summaryLine ? `1px solid ${colors.border}` : 'none', borderBottom: `1px solid ${colors.border}`, overflowX: 'auto' }}>
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 12px',
              border: 'none',
              borderBottom: activeTab === tab ? `2px solid ${colors.accent}` : '2px solid transparent',
              background: 'transparent',
              color: activeTab === tab ? colors.accent : colors.secondary,
              fontSize: '12px',
              fontWeight: activeTab === tab ? 700 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'color 0.15s, border-color 0.15s',
            }}
          >
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      {/* Active tab content */}
      <div style={{ padding: '16px' }}>
        {activeTab === 'problem' && prerequisites.length > 0 && (
          <div style={{
            border: `1px solid ${setupItems.length ? warn.border : colors.border}`,
            background: setupItems.length ? warn.bg : colors.accentBg,
            borderRadius: '7px',
            marginBottom: '14px',
            overflow: 'hidden',
          }}>
            <button
              onClick={() => setPrereqOpen(o => !o)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '9px 12px', border: 'none', background: 'transparent', cursor: 'pointer',
                color: setupItems.length ? warn.text : colors.accent,
                fontSize: '12px', fontWeight: 700, textAlign: 'left',
              }}
            >
              <span>
                {setupItems.length ? '⚠️ Before you start' : '💡 Before you start'}
                <span style={{ fontWeight: 500, opacity: 0.8 }}> ({prerequisites.length})</span>
                {setupItems.length > 0 && (
                  <span style={{ fontWeight: 500, opacity: 0.85 }}> — org setup required or the tests will fail</span>
                )}
              </span>
              <span style={{ fontSize: '10px', transform: prereqOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</span>
            </button>

            {prereqOpen && (
              <div style={{ padding: '2px 12px 10px' }}>
                {setupItems.map((item, i) => (
                  <div key={`s${i}`} style={{ display: 'flex', gap: '8px', padding: '5px 0', fontSize: '12px', lineHeight: 1.55, color: warn.text }}>
                    <span style={{ flexShrink: 0, marginTop: '1px' }}>🔧</span>
                    <span style={{ fontWeight: 600 }}>{item.replace(/^ORG SETUP:\s*/i, '')}</span>
                  </div>
                ))}
                {knowledgeItems.map((item, i) => (
                  <div key={`k${i}`} style={{ display: 'flex', gap: '8px', padding: '4px 0', fontSize: '12px', lineHeight: 1.55, color: colors.secondary }}>
                    <span style={{ color: colors.accent, flexShrink: 0, marginTop: '1px' }}>•</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'problem' && (
          problemSections.length > 1 ? (
            <div>
              {/* Sub-tabs for scenarios/challenges within the Problem tab */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {problemSections.map((section, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveProblemSection(i)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '5px',
                      border: `1px solid ${activeProblemSection === i ? colors.accent : colors.border}`,
                      background: activeProblemSection === i ? colors.accentBg : 'transparent',
                      color: activeProblemSection === i ? colors.accent : colors.secondary,
                      fontSize: '11px',
                      fontWeight: activeProblemSection === i ? 700 : 500,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s',
                    }}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
              <div>
                {formatSectionContent(problemSections[activeProblemSection]?.content || '')}
              </div>
            </div>
          ) : (
            <p style={{ fontSize: '13px', color: colors.secondary, lineHeight: 1.75, whiteSpace: 'pre-line', margin: 0 }}>
              {description}
            </p>
          )
        )}

        {activeTab === 'requirements' && (
          <div>
            {requirements.map((req, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', padding: '7px 0', borderBottom: i < requirements.length - 1 ? `1px solid ${colors.border}` : 'none' }}>
                <span style={{ color: colors.accent, fontSize: '11px', marginTop: '2px', flexShrink: 0 }}>→</span>
                <span style={{ fontSize: '12px', color: colors.secondary, lineHeight: 1.5 }}>{req}</span>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'concept' && (
          <div>
            <div style={{ fontSize: '12px', color: colors.secondary, lineHeight: 1.8, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {concept}
            </div>
            {docLink && (
              <a href={docLink} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: '12px', color: colors.accent, fontSize: '11px', textDecoration: 'none', border: `0.5px solid ${colors.accent}`, padding: '4px 10px', borderRadius: '5px' }}>
                Read docs →
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DescriptionSection;